type SsoEventName =
  | 'sso_login_started'
  | 'sso_callback_landed'
  | 'sso_exchange_ok'
  | 'sso_exchange_failed'
  | 'sso_login_ok'
  | 'sso_login_failed'
  | 'sso_logout_local'
  | 'sso_logout_idp_redirect'

export type SsoEventContext = {
  portal_id?: string
  provider_slug?: string
  attempt?: number
  [key: string]: unknown
}

/** Bounded so a cold RUM chunk can never stall a login redirect. */
const FLUSH_TIMEOUT_MS = 300

/**
 * Best-effort RUM action — telemetry must never break, nor visibly delay, the
 * login flow. The RUM SDK is only initialized on prod (see useDataDog.ts);
 * before init() it buffers actions and replays them once initialized.
 *
 * A caller that navigates right after tracking MUST await the returned
 * promise, otherwise the action is lost: the SDK is imported lazily, so an
 * un-awaited addAction is still a pending microtask when the redirect
 * commits. That is how only 4 of 58 sso_login_started events reached RUM —
 * and that event is the denominator of the first-attempt success rate.
 */
export const trackSsoEvent = (
  name: SsoEventName,
  context: SsoEventContext = {}
): Promise<void> => {
  console.debug(`[sso] ${name}`, context)

  const recorded = import('@datadog/browser-rum')
    .then(({ datadogRum }) => datadogRum.addAction(name, context))
    .catch(() => undefined)

  return Promise.race([
    recorded,
    new Promise<void>((resolve) => {
      setTimeout(resolve, FLUSH_TIMEOUT_MS)
    })
  ])
}
